import type { ErrorEvent, EventHint } from '@sentry/react';
import { ApiError } from 'src/errors/ApiError';

/**
 * gRPC status codes that indicate a backend/infrastructure failure rather
 * than an expected business outcome (validation, not found, auth, etc.).
 * Only ApiErrors with these codes are reported to Sentry; everything else
 * is user-facing noise already surfaced via toast notifications.
 */
const UNEXPECTED_GRPC_CODES: ReadonlyArray<number> = [
  2, // UNKNOWN
  4, // DEADLINE_EXCEEDED
  8, // RESOURCE_EXHAUSTED
  12, // UNIMPLEMENTED
  13, // INTERNAL
  14, // UNAVAILABLE
  15, // DATA_LOSS
];

/**
 * Shared `beforeSend` for Sentry.init in all realm route files.
 * Previously every ApiError was dropped, which made handled API failures
 * invisible in Sentry; now unexpected (server-side) gRPC codes get through,
 * tagged with their code for grouping/filtering.
 */
export const sentryBeforeSend = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  const exception = hint?.originalException;

  if (exception instanceof ApiError) {
    if (!UNEXPECTED_GRPC_CODES.includes(exception.code)) {
      return null;
    }

    return {
      ...event,
      tags: {
        ...event.tags,
        grpc_code: exception.code,
        // Same id as the backend's x-request-id: greps every backend log line
        // of this request and matches backend Sentry events tagged requestId.
        ...(exception.requestId ? { request_id: exception.requestId } : {}),
      },
    };
  }

  return event;
};

export default sentryBeforeSend;
